import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelReportAdminTenantRequest from '../../Interfaces/Requests/Administration/AddDelReportAdminTenantRequest';
import GetReportAdminTenantModelsUsingReportIDRequest from '../../Interfaces/Requests/Administration/GetReportAdminTenantModelsUsingReportIDRequest';
import GetReportAdminTenantModelsUsingReportIDResponse from '../../Interfaces/Responses/Administration/GetReportAdminTenantModelsUsingReportIDResponse';

type ReportTenantProps = {
    reportId: number;
    reportName: string;
    reportGroupName: string;
    close: () => void;
};

const ReportTenants: React.FC<ReportTenantProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [tenants, setTenants] = useState<GetReportAdminTenantModelsUsingReportIDResponse[]>([]);
    const [hideUnlinkedTenants, setHideUnlinkedTenants] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetReportTenantsLoading, isError: isGetReportTenantsError, error: getReportTenantsError, refetch: refetchReportTenants } = useQuery(
        ['report-admin-tenants', props.reportId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_TENANT_MODELS_USING_REPORT_ID}`;
                const requestData: GetReportAdminTenantModelsUsingReportIDRequest = {
                    grt_reportid: props.reportId,
                };
                return await AxiosUtil.get<GetReportAdminTenantModelsUsingReportIDResponse[], GetReportAdminTenantModelsUsingReportIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_REPORT_ADMIN_TENANT_MODELS_USING_REPORT_ID: [Get Report Tenants] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_TENANT_MODELS_USING_REPORT_ID [Get Report Tenants] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.reportId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_REPORT_ADMIN_TENANT_MODELS_USING_REPORT_ID: [Get Report Tenants] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_TENANT_MODELS_USING_REPORT_ID: [Get Report Tenants] Internal Error: ', err);
            },
            onSuccess: (response: GetReportAdminTenantModelsUsingReportIDResponse[]) => {
                if (response) {
                    setTenants(response);
                }
            },
        }
    );

    const { mutate: addRemoveTenant, isLoading: isAddRemoveTenantLoading, isError: isAddRemoveTenantError, error: getAddRemoveTenantError } = useMutation(
        async ({ add, tenant, }: {
            add: boolean;
            tenant: GetReportAdminTenantModelsUsingReportIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_REPORT_ADMIN_TENANTS}`;
                const addDelReportAdminTenants: AddDelReportAdminTenantRequest = {
                    id: tenant.id,
                    tenantName: tenant.tenantName,
                    reportId: props.reportId,
                    reportName: props.reportName,
                    reportGroupName: props.reportGroupName,
                    connString: tenant.connString,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelReportAdminTenantRequest[] = [ addDelReportAdminTenants, ];
                responseData = await AxiosUtil.post<number, AddDelReportAdminTenantRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_REPORT_ADMIN_TENANTS: [Add/Remove Report Tenants] Internal Error: ' + err);
                console.error('ADD_DEL_REPORT_ADMIN_TENANTS [Add/Remove Report Tenants] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, tenant };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_REPORT_ADMIN_TENANTS: [Add/Remove Report Tenants] Internal Error: ' + err);
                console.error('ADD_DEL_REPORT_ADMIN_TENANTS: [Add/Remove Report Tenants] Internal Error: ', err);
            },
            onSuccess: ({ response, add, tenant }) => {
                if (response) {
                    setTenants(updateLinkedAdminRow(tenants, tenant.id, add));
                    void refetchReportTenants();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, tenant: GetReportAdminTenantModelsUsingReportIDResponse) => {
        const params = { add: checked, tenant: tenant, };
        addRemoveTenant(params);
    };

    const columns: ColumnsType<GetReportAdminTenantModelsUsingReportIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetReportAdminTenantModelsUsingReportIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'Tenant Name', dataIndex: 'tenantName', key: 'tenantName' },
    ];

    const visibleTenants = filterLinkedAdminRows(
        tenants,
        hideUnlinkedTenants,
        searchText.trim(),
        (tenant, normalizedSearchText) => normalizedSearchText === '' || tenant.tenantName.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowLinkedTenants = () => {
        setHideUnlinkedTenants(!hideUnlinkedTenants);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetReportTenantsError,
            getReportTenantsError,
            isAddRemoveTenantError,
            getAddRemoveTenantError
        );
    };

    return (
        <>
            <Spin spinning={isGetReportTenantsLoading || isAddRemoveTenantLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetReportTenantsError ||
                        isAddRemoveTenantError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Links to Tenants</Title>
                    </Text>
                </Space>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowLinkedTenants}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedTenants ? 'Show All Tenants' : 'Hide Unlinked Tenants'}
                            </Button>
                        </Col>
                        <Col span={4} />
                        <Col span={8}>
                            <Input
                                value={searchText}
                                allowClear={true}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={2} />
                        <Col span={6}><Text type='secondary' italic ellipsis>{'[' + props.reportGroupName + ']:' + props.reportName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetReportTenantsLoading && tenants.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleTenants} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default ReportTenants;
